<template>
  <div class="multiple-upload-checklist p-4 card pb-0" v-if="accountData.id">
    <h3 class="mb-3">{{ $t("signup.multiple_upload.title") }}</h3>
    <p class="mb-4 mb-sm-5">
      {{ $t("signup.multiple_upload.description") }}
    </p>

    <div v-for="item in uploadItems" :key="item.fileKey">
      <UploadChecklistItemComponent
        :uploadTitle="
          getTranslation(item.title, `signup.multiple_upload.${item.fileKey}`)
        "
        :supportedFormats="
          getTranslation(item.supportedFormats, 'common.any_file_format')
        "
        :uploadUrl="item.uploadUrl || defaultUploadUrl"
        :fileUrl="findFileUrl(item.fileKey).value"
        :queryParams="getParams(item.fileKey).value"
        :uploadErrorMessage="
          getTranslation(item.uploadErrorMessage, 'common.invalid_file_error')
        "
        :required="item.required !== false"
        :displayAsterisk="hasMixedRequirements && item.required !== false"
        @uploadComplete="$emit('uploadComplete', $event)"
        @errorOccurred="$emit('errorOccurred', $event)"
      />

      <BasicInfoCardComponent
        :buttonText="$t('common.read_more_button')"
        :titleText="
          getTranslation(
            item.infoTitle,
            'signup.multiple_upload.where_to_find_this_document'
          )
        "
        class="mb-6"
      >
        <p
          class="mt-2 mb-2"
          v-html="
            getTranslation(
              item.infoContent,
              `signup.multiple_upload.${item.fileKey}_where_to_find`
            )
          "
        ></p>
      </BasicInfoCardComponent>
    </div>
  </div>
</template>

<script setup lang="ts">
import BasicInfoCardComponent from "@/components/common/BasicInfoCardComponent.vue";
import UploadChecklistItemComponent from "@/components/common/UploadChecklistItemComponent.vue";
import { useCompanyFinder } from "@/composables/useCompanyFinder";
import { useGeneratorFinder } from "@/composables/useGeneratorFinder";
import type { Account, GeneratorUploadResult } from "@/types";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

export interface UploadItem {
  fileKey: string;
  title?: string; // Translation key
  supportedFormats?: string; // Translation key
  uploadUrl?: string; // Direct value
  infoTitle?: string; // Translation key
  infoContent?: string; // Translation key
  uploadErrorMessage?: string; // Translation key
  required?: boolean; // Whether the file is required (defaults to true)
}

const props = defineProps<{
  accountData: Account;
  uploadItems: UploadItem[];
  defaultUploadUrl: string;
}>();

defineEmits<{
  uploadComplete: [result: GeneratorUploadResult];
  errorOccurred: [error: unknown];
}>();

const { t } = useI18n();
const { companyIndex } = useCompanyFinder(props.accountData);
const selectedCompany = computed(
  () => props.accountData.companies[companyIndex.value]
);
const { nullableSelectedGenerator } = useGeneratorFinder(props.accountData);

const hasMixedRequirements = computed(() => {
  const requiredCount = props.uploadItems.filter(
    (item) => item.required !== false
  ).length;
  return requiredCount > 0 && requiredCount < props.uploadItems.length;
});

const getTranslation = (key?: string, fallbackKey?: string) => {
  if (key) {
    return t(key);
  }
  return fallbackKey ? t(fallbackKey) : "";
};

const getParams = (fileKey: string) => {
  return computed(() => {
    const params: Record<string, string> = {
      file_key: fileKey,
    };

    if (selectedCompany?.value?.id !== undefined) {
      params.company_id = String(selectedCompany.value.id);
    }

    if (props.accountData.id !== undefined) {
      params.account_id = String(props.accountData.id);
    }

    if (nullableSelectedGenerator.value.generator?.id !== undefined) {
      params.generator_id = String(
        nullableSelectedGenerator.value.generator.id
      );
    }

    return params;
  });
};

const findFileUrl = (fileKey: string) => {
  return computed(() => {
    if (nullableSelectedGenerator.value.generator?.files?.[fileKey]) {
      return nullableSelectedGenerator.value.generator.files[fileKey];
    }
    return "";
  });
};
</script>
